import { Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  firstGridRowObjectFromPayload,
} from '../../../../shared/workspace-element-context';
import {
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../../../shared/utils/appkit-dropdown.util';
import {
  CbcEditAcctTaggingService,
  CbcrAccountOption,
} from './cbc-edit-acct-tagging.service';

@Component({
    selector: 'gtw-cbc-edit-acct-tagging',
    templateUrl: './cbc-edit-acct-tagging.component.html',
    styleUrls: ['./cbc-edit-acct-tagging.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CbcEditAcctTaggingComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  modalTitle = 'Create/Edit CbCr Account Mapping';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  isLoading = false;
  accountsLoading = false;

  accounts: CbcrAccountOption[] = [];
  accountDropdownItems: any[] = [];
  selectedAccount: CbcrAccountOption | null = null;
  selectedAccountItem: any = null;

  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  rowData: any = {};
  private isInitialized = false;

  constructor(
    private acctTaggingService: CbcEditAcctTaggingService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const raw = rowPayloadFromActiveScreen(activeScreen);
    const resolved = firstGridRowObjectFromPayload(raw);
    if (resolved && Object.keys(resolved).length > 0) {
      this.rowData = resolved;
    }
    this.cdr.markForCheck();
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (
      !this.GENERAL_CONFIG?.base_url ||
      !this.filterParams ||
      Object.keys(this.filterParams).length === 0 ||
      !this.globalService ||
      Object.keys(this.globalService).length === 0
    ) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.loadAccounts();
    } catch (e) {
      console.error('Error in cbc-edit-acct-tagging init:', e);
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  loadAccounts(): void {
    this.accountsLoading = true;
    this.isLoading = true;
    this.cdr.detectChanges();

    this.acctTaggingService
      .loadAccounts(
        this.baseUrl,
        this.ssoId,
        this.rowData?.SYSTEM_ACCT_KEY,
        this.rowData?.CBCR_ACCT_KEY,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.accounts = Array.isArray(data) ? data : [];
          this.accountDropdownItems = mapToAppkitDropdownItems(this.accounts, 'ACCT', 'ACCT_KEY');
          this.selectedAccountItem = this.accountDropdownItems.find(
            (item) => item.value === this.selectedAccount?.ACCT_KEY
          ) || null;
          this.accountsLoading = false;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.accountsLoading = false;
          this.isLoading = false;
          this.gtwSnackbarService.openSnackBar(
            err?.message || 'Something went wrong while loading accounts!',
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
        },
      });
  }

  loadProjectSteps(): void {
    // Hook preserved from original AngularJS template (ng-change="ctrl.loadProjectSteps()").
    // Original AngularJS controller did not implement this method; left intentionally as no-op.
  }

  onAccountChange(value: CbcrAccountOption | null): void {
    this.selectedAccount = value;
    this.selectedAccountItem = value
      ? this.accountDropdownItems.find((item) => item.value === value.ACCT_KEY) || null
      : null;
    this.loadProjectSteps();
    this.cdr.detectChanges();
  }

  onAccountDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedAccountItem = selected;
    const account = this.accounts.find((a) => a.ACCT_KEY === selected?.value) || null;
    this.onAccountChange(account);
  }

  onAccountSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedAccountItem = selected;
    }
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  get isFormInvalid(): boolean {
    return !this.selectedAccount;
  }

  save(): void {
    if (this.isSaveClicked || this.crudLoading || this.isFormInvalid) return;
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const clobSettings = [{
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key,
    }];
    const clobData = [{
      CHART_KEY: this.rowData?.CHART_KEY,
      SYSTEM_ACCT_KEY: this.rowData?.SYSTEM_ACCT_KEY,
      CBCR_ACCT_KEY: this.rowData?.CBCR_ACCT_KEY,
      NEW_CBCR_ACCT_KEY: this.selectedAccount?.ACCT_KEY ?? null,
    }];

    this.acctTaggingService
      .saveAcctTagging(
        this.baseUrl,
        clobData,
        clobSettings,
        this.ssoId,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          if (response?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar(
              'Data saved successfully.',
              'gtw-snackbar--green'
            );
            this.emitRefreshAndClose();
          } else {
            this.gtwSnackbarService.openSnackBar(
              response?.errorMessage || 'Error saving data.',
              'gtw-snackbar--red'
            );
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar(
            err?.message || 'Something went wrong while saving your changes, please try again!',
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
        },
      });
  }

  private emitRefreshAndClose(): void {
    this.cancel();
    setTimeout(() => {
      const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
      if (activeScreen?.screen_key) {
        this.gtwWorkspace.refreshGridData.next(activeScreen);
      }
    }, 500);
  }

  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true,
    });
    document
      .querySelector('gtw-cbc-edit-acct-tagging-ce')
      ?.dispatchEvent(updateEvent);
  }
}
